import {
  Checkbox,
  Chip,
  Collapse,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconEdit } from '@tabler/icons';
import { IconEye, IconEyeFilled } from '@tabler/icons-react';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import enLocale from 'date-fns/locale/en-US';
import frLocale from 'date-fns/locale/fr-CA';
import useTimezone from 'hooks/useTimezone';
import moment from 'moment-timezone';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import SubCard from 'views/components/cards/SubCard';
import DeleteTodoModal from './DeleteTodoModal';
import ToDoAdd from './ToDoAdd';

function ExpandRow({ todo, handleChangeState, selectedDate }) {
  const intl = useIntl();
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const { timezone } = useTimezone();

  const handleCloseDialog = () => {
    setOpenEditModal(false);
  };

  const handleDeleteDialog = () => {
    setOpenDeleteModal(false);
  };

  const getStatus = () => {
    if (todo?.isCompleted) {
      return <Chip label={intl.formatMessage({ defaultMessage: 'Completed' })} color="success" variant="outlined" />;
    }
    const today = moment().startOf('day');
    const dueDate = moment(todo?.dueDate).startOf('day');

    if (dueDate.isBefore(today)) {
      return <Chip label={intl.formatMessage({ defaultMessage: 'Overdue' })} color="error" variant="outlined" />;
    } else if (dueDate.isSame(today) || dueDate.isAfter(today)) {
      return <Chip label={intl.formatMessage({ defaultMessage: 'Due Soon' })} color="info" variant="outlined" />;
    }
    return '';
  };

  return (
    <React.Fragment key={todo._id}>
      <TableRow sx={{ height: 65 }}>
        <TableCell sx={{ padding: 1 }}>
          <Typography>
            <Tooltip title={todo?.isCompleted ? '' : intl.formatMessage({ defaultMessage: 'Check to mark as complete' })}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={todo?.isCompleted}
                    onChange={handleChangeState}
                    name={todo?._id}
                    color="primary"
                    disabled={todo?.isCompleted}
                  />
                }
                label={''}
              />
            </Tooltip>
          </Typography>
        </TableCell>

        <TableCell sx={{ textDecoration: todo?.isCompleted ? 'line-through' : 'none', padding: 1 }}>
          <Grid item>
            <Typography align="left" component="div" variant="subtitle1">
              {todo?.title?.length > 20 ? `${todo?.title?.substring(0, 28)}...` : todo?.title}
            </Typography>
            <Grid container spacing={2}>
              <Grid item>
                <Typography align="left" component="div" variant="subtitle2">
                  {todo?.dueDate
                    ? format(utcToZonedTime(todo.dueDate, timezone), intl.locale === 'fr' ? 'do MMMM yyyy' : 'MMMM do yyyy', {
                        locale: intl.locale === 'fr' ? frLocale : enLocale
                      })
                    : ''}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </TableCell>

        <TableCell sx={{ padding: 1 }}>
          {todo?.priority === 'Medium' ? (
            <Chip label={intl.formatMessage({ defaultMessage: 'Medium' })} color="info" variant="outlined" />
          ) : todo?.priority === 'Low' ? (
            <Chip label={intl.formatMessage({ defaultMessage: 'Low' })} color="success" variant="outlined" />
          ) : todo?.priority === 'High' ? (
            <Chip label={intl.formatMessage({ defaultMessage: 'High' })} color="error" variant="outlined" />
          ) : (
            ''
          )}
        </TableCell>
        <TableCell sx={{ padding: 1 }}>
          {todo?.createdAt
            ? format(utcToZonedTime(todo.createdAt, timezone), intl.locale === 'fr' ? 'do MMMM yyyy' : 'MMMM do yyyy', {
                locale: intl.locale === 'fr' ? frLocale : enLocale
              })
            : ''}
        </TableCell>
        <TableCell sx={{ padding: 1 }}>{getStatus()}</TableCell>

        <TableCell sx={{ padding: 1 }}>
          <Stack direction="row">
            <Tooltip title={intl.formatMessage({ defaultMessage: 'View' })}>
              <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
                {open ? <IconEyeFilled /> : <IconEye />}
              </IconButton>
            </Tooltip>

            <Tooltip title={intl.formatMessage({ defaultMessage: 'Edit' })}>
              <IconButton color="primary" onClick={() => setOpenEditModal(true)} disabled={todo?.isCompleted}>
                <IconEdit size={24} />
              </IconButton>
            </Tooltip>
          </Stack>
        </TableCell>
      </TableRow>
      {open && (
        <TableRow>
          <TableCell colSpan={12}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <SubCard content={false}>
                <Typography p={2} style={{ width: '100%', wordWrap: 'break-word' }}>
                  {todo?.title || ''}
                </Typography>
              </SubCard>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
      {openEditModal && <ToDoAdd todo={todo} handleCloseDialog={handleCloseDialog} selectedDate={selectedDate} />}
      {openDeleteModal && <DeleteTodoModal todo={todo} setOpenDeleteModal={handleDeleteDialog} selectedDate={selectedDate} />}
    </React.Fragment>
  );
}

export default ExpandRow;
